/**
 * Relance les requêtes quand l'app revient au premier plan.
 *
 * `refetchOnWindowFocus` est coupé (cf. `main.tsx`) : sur ordinateur, chaque
 * retour sur l'onglet relancerait toutes les requêtes pour rien. Mais en web
 * app sur téléphone, il n'y a ni bouton recharger ni onglet qu'on rouvre :
 * l'app reste en mémoire et reprend telle quelle — avec les chiffres du matin
 * le soir venu, alors que les ventes et la pub se mettent à jour toutes les
 * dix minutes.
 *
 * Seuil : une absence d'au moins deux minutes. En dessous (un coup d'œil à
 * une notification), rien n'a pu bouger qui vaille un aller-retour.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

const MIN_HIDDEN_MS = 2 * 60 * 1000;

export function useResumeRefresh(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null && Date.now() - hiddenAt >= MIN_HIDDEN_MS) {
        // Ignore le `staleTime` : les requêtes affichées repartent tout de
        // suite, les autres seront refaites à leur prochain affichage.
        void queryClient.invalidateQueries();
      }
      hiddenAt = null;
    };

    // iOS restaure parfois la page depuis le cache de navigation sans
    // `visibilitychange` : `pageshow` persisté couvre ce cas.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) void queryClient.invalidateQueries();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [queryClient]);
}
